import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { AssetManagerInvoice, LanguageNameSpaces } from "@shared/types";
import { checkSearch } from "@shared/utils/misc.ts";
import { ColumnsType } from "antd/es/table";
import {
  FROM_DESKTOP_COLUMN,
  FROM_LAPTOP_COLUMN,
} from "common/NewLook/responsiveColumns.ts";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general.ts";
import Text from "common/Typography/Text.tsx";
import { formatDate, getFormatTime } from "@shared/utils/date.ts";
import { toFixed } from "@shared/utils/numbers.ts";
import { USD_PRECISION } from "features/affiliation/common/constants.ts";
import React from "react";
import Table from "common/Table/Table.tsx";
import EmptyView from "common/Empty/EmptyView.tsx";
import Flex from "common/Boxes/Flex.tsx";
import { EmptyStatesType } from "common/Icons/types.ts";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import useInvoice from "features/payouts/Invoices/hooks/useInvoice.tsx";
import InvoiceActions from "features/payouts/Invoices/InvoiceActions.tsx";
import useAccounts from "features/payouts/Invoices/hooks/useAccounts.ts";
import styled, { useTheme } from "styled-components";
import { formatUsd } from "features/affiliation/NewLook/newLookUtils";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import TableSkeleton from "features/admin/NewLook/TableSkeleton";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface InvoicesTableProps {
  invoices: AssetManagerInvoice[] | undefined;
  loading: boolean;
  searchFilter?: string;
}

const InvoicesTable = ({
  invoices,
  loading,
  searchFilter,
}: InvoicesTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const { getAccountsNames, clientToExchangeMapping } = useAccounts();
  const { getStatusTag, getInvoiceActions } = useInvoice();
  const { isNewLook } = useTheme();
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const filteredInvoices = invoices?.filter((invoice) => {
    const accountNames = getAccountsNames(invoice.client_ids);
    return accountNames.find((name) => checkSearch(name, searchFilter));
  });

  // New look: the date over its time, figures right-aligned (the share over
  // its rate), a quiet status and the row menu.
  const newLookColumns: ColumnsType<AssetManagerInvoice> = [
    {
      title: t("invoices.table.created_at"),
      dataIndex: "created_at",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      sorter: (a, b) => sortByNumberProperty(a, b, "created_at"),
      render: (date) => (
        <Stack>
          <Primary>{formatDate(date, false, true)}</Primary>
          <Meta>{getFormatTime(date, true)}</Meta>
        </Stack>
      ),
    },
    {
      title: t("invoices.table.accounts"),
      dataIndex: "client_ids",
      render: (accountsIds) => (
        <ExchangesIconsGroup
          clients={getAccountsNames(accountsIds)}
          clientToExchangeMapping={clientToExchangeMapping}
          size={24}
        />
      ),
    },
    {
      title: t("invoices.table.payment_method"),
      dataIndex: "payout_method_name",
      showSorterTooltip: false,
      ...FROM_DESKTOP_COLUMN,
      sorter: (a, b) => sortByStringProperty(a, b, "payout_method"),
      render: (paymentMethod) => <Body>{paymentMethod}</Body>,
    },
    {
      title: t("invoices.table.period"),
      dataIndex: "amount",
      showSorterTooltip: false,
      ...FROM_LAPTOP_COLUMN,
      sorter: (a, b) => sortByNumberProperty(a, b, "amount"),
      render: (_, invoice) => (
        <Body>
          {formatDate(invoice.period_start, false, true)} –{" "}
          {formatDate(invoice.period_end, false, true)}
        </Body>
      ),
    },
    {
      title: t("invoices.table.total_pnl"),
      dataIndex: "total_pnl",
      align: "right",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "total_pnl"),
      render: (totalPnl) => <Figure>{formatUsd(totalPnl)}</Figure>,
    },
    {
      title: t("invoices.table.profit_share"),
      dataIndex: "profit_share_amount",
      align: "right",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "profit_share_amount"),
      render: (amount, invoice) => (
        <Stack $end={true}>
          <Figure>{formatUsd(amount)}</Figure>
          <Meta>{invoice.profit_sharing_percent}%</Meta>
        </Stack>
      ),
    },
    {
      title: t("invoices.table.status"),
      dataIndex: "status",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "status"),
      render: (_, invoice) => getStatusTag(invoice),
    },
    {
      title: "",
      key: "action",
      dataIndex: "id",
      width: 56,
      render: (_, invoice) => (
        <InvoiceActions actions={getInvoiceActions(invoice)} />
      ),
    },
  ];

  const columns: ColumnsType<AssetManagerInvoice> = [
    {
      title: t("invoices.table.created_at"),
      dataIndex: "created_at",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      sorter: (a, b) => sortByNumberProperty(a, b, "created_at"),
      render: (date) => <Text>{formatDate(date, true, true)}</Text>,
    },
    {
      title: t("invoices.table.accounts"),
      dataIndex: "client_ids",
      showSorterTooltip: false,
      render: (accountsIds) => (
        <ExchangesIconsGroup
          clients={getAccountsNames(accountsIds)}
          clientToExchangeMapping={clientToExchangeMapping}
          size={isNewLook ? 26 : 28}
        />
      ),
    },
    {
      title: t("invoices.table.payment_method"),
      dataIndex: "payout_method_name",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "payout_method"),
      render: (paymentMethod) => <Text>{paymentMethod}</Text>,
    },
    {
      title: t("invoices.table.period"),
      dataIndex: "amount",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "amount"),
      render: (_, invoice) => (
        <Flex>
          <Text>
            {formatDate(invoice.period_start, false, true)}
            <span style={{ margin: "0 4px" }}>{isNewLook ? "–" : "-"}</span>
            {formatDate(invoice.period_end, false, true)}
          </Text>
        </Flex>
      ),
    },
    {
      title: t("invoices.table.total_pnl"),
      dataIndex: "total_pnl",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "total_pnl"),
      render: (totalPnl) =>
        isNewLook ? (
          formatUsd(totalPnl)
        ) : (
          <Text>${toFixed(totalPnl, USD_PRECISION)}</Text>
        ),
    },
    {
      title: t("invoices.table.profit_share"),
      dataIndex: "profit_share_amount",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "profit_share_amount"),
      render: (amount, invoice) => (
        <Flex $justifyContent={"flex-start"}>
          <Text style={{ marginRight: 4 }}>
            {isNewLook
              ? formatUsd(amount)
              : `$${toFixed(amount, USD_PRECISION)}`}
          </Text>
          <Text type="secondary">({invoice.profit_sharing_percent}%)</Text>
        </Flex>
      ),
    },
    {
      title: t("invoices.table.status"),
      dataIndex: "status",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "status"),
      render: (_, invoice) => getStatusTag(invoice),
    },
    {
      title: "",
      key: "action",
      dataIndex: "id",
      render: (_, invoice) => (
        <InvoiceActions actions={getInvoiceActions(invoice)} />
      ),
    },
  ];

  const emptyState = (
    <EmptyView
      title={t("invoices.empty.title")}
      iconKey={EmptyStatesType.Invoices}
      iconWidth={isNewLook ? 112 : undefined}
    />
  );

  // New look: rows shaped like invoices while the first load runs.
  const showSkeleton = isNewLook && loading && !invoices?.length;

  if (isNewLook)
    return (
      <Table<AssetManagerInvoice>
        columns={newLookColumns}
        rowKey={(invoice) => invoice.id}
        dataSource={filteredInvoices}
        loading={loading && !showSkeleton}
        locale={{
          emptyText: showSkeleton ? (
            <TableSkeleton />
          ) : searchFilter ? (
            <PanelEmpty
              title={tCommon("empty.no_results_matching_search", {
                objectName: t("invoices.table.title").toLowerCase(),
              })}
            />
          ) : (
            <PanelEmpty
              title={t("invoices.empty.title")}
              description={t("invoices.empty.description")}
            />
          ),
        }}
      />
    );

  return (
    <Table<AssetManagerInvoice>
      columns={columns}
      rowKey={(invoice) => invoice.id}
      dataSource={filteredInvoices}
      loading={loading}
      locale={{ emptyText: emptyState }}
    />
  );
};

export default InvoicesTable;

const Stack = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$end ? "flex-end" : "flex-start")};
  gap: 2px;
`;
const Primary = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
`;
const Body = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).text};
  font-size: 13.5px;
  line-height: 20px;
  white-space: nowrap;
`;
const Figure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
